import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Calendar, ShieldCheck, AlertTriangle, CheckCircle,
  ChevronLeft, ChevronRight, Loader2, Play, Wrench,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'

interface PPMSchedule {
  id: string
  title: string
  description: string | null
  frequency: string
  intervalDays: number | null
  estimatedMinutes: number | null
  isCompliance: boolean
  regulationRef: string | null
  nextDueDate: string
  lastCompletedAt: string | null
  isActive: boolean
  isOverdue: boolean
  daysUntilDue: number
  asset: { id: string; name: string; code: string; criticality: string } | null
  owner: { id: string; firstName: string; lastName: string } | null
  vendor: { id: string; name: string } | null
  complianceCategory: { id: string; name: string; code: string } | null
  _count: { workOrders: number }
}

interface ComplianceSummary {
  id: string
  name: string
  code: string
  status: 'COMPLIANT' | 'DUE_SOON' | 'OVERDUE'
  certificates: any[]
  upcomingExpiries: any[]
  overdueSchedules: any[]
}

const FREQUENCY_LABELS: Record<string, string> = {
  DAILY: 'Daily', WEEKLY: 'Weekly', FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', SEMI_ANNUAL: '6-Monthly',
  ANNUAL: 'Annual', BIENNIAL: '2-Yearly', CUSTOM_DAYS: 'Custom',
}

const CRITICALITY_DOT: Record<string, string> = {
  CRITICAL: 'bg-red-500', HIGH: 'bg-amber-500', MEDIUM: 'bg-blue-500', LOW: 'bg-gray-400',
}

export function PPMPage() {
  const { currentPropertyId, user } = useAuthStore()
  const pid = currentPropertyId
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<'schedules' | 'compliance'>('schedules')
  const [page, setPage] = useState(1)
  const [complianceOnly, setComplianceOnly] = useState(false)
  const [completeId, setCompleteId] = useState<string | null>(null)
  const [completeNotes, setCompleteNotes] = useState('')
  const [labourCost, setLabourCost] = useState('')
  const [partsCost, setPartsCost] = useState('')

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['ppm-schedules', pid, page, complianceOnly],
    queryFn: () =>
      apiGet<{ data: PPMSchedule[]; pagination: { page: number; total: number; totalPages: number } }>(
        `/properties/${pid}/ppm`,
        { page, limit: 25, isActive: true, ...(complianceOnly && { isCompliance: true }) }
      ),
    enabled: !!pid,
  })

  const { data: compliance, isLoading: complianceLoading } = useQuery({
    queryKey: ['compliance-summary', pid],
    queryFn: () => apiGet<ComplianceSummary[]>(`/properties/${pid}/ppm/compliance-summary`),
    enabled: !!pid && tab === 'compliance',
  })

  // ── Mutations ───────────────────────────────────────────────────────────
  const completeMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      apiPost(`/properties/${pid}/ppm/${scheduleId}/complete`, {
        completionNotes: completeNotes || undefined,
        ...(labourCost && { labourCostGbp: parseFloat(labourCost) }),
        ...(partsCost && { partsCostGbp: parseFloat(partsCost) }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ppm-schedules'] })
      setCompleteId(null)
      setCompleteNotes('')
      setLabourCost('')
      setPartsCost('')
    },
  })

  const generateMutation = useMutation({
    mutationFn: () => apiPost(`/properties/${pid}/ppm/generate`),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['ppm-schedules'] })
    },
  })

  const schedules = data?.data ?? []
  const pagination = data?.pagination
  const isChiefPlus = ['GROUP_ADMIN', 'REGIONAL_DIRECTOR', 'PROPERTY_MANAGER', 'CHIEF_ENGINEER'].includes(user?.role ?? '')

  const overdueCount = schedules.filter(s => s.isOverdue).length
  const dueThisWeek = schedules.filter(s => !s.isOverdue && s.daysUntilDue <= 7).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">PPM Schedules</h1>
          <p className="text-gray-500 text-sm mt-0.5">Planned preventive maintenance</p>
        </div>
        {isChiefPlus && (
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="btn-primary"
          >
            {generateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            Generate WOs
          </button>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Total Active</p>
          <p className="text-2xl font-bold text-navy-800">{pagination?.total ?? '—'}</p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Overdue</p>
          <p className={cn('text-2xl font-bold', overdueCount > 0 ? 'text-red-600' : 'text-navy-800')}>
            {overdueCount}
          </p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Due This Week</p>
          <p className={cn('text-2xl font-bold', dueThisWeek > 0 ? 'text-amber-600' : 'text-navy-800')}>
            {dueThisWeek}
          </p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Compliance</p>
          <p className="text-2xl font-bold text-navy-800">
            {schedules.filter(s => s.isCompliance).length}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab('schedules')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            tab === 'schedules' ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-400 hover:text-gray-600'
          )}
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Schedules
        </button>
        <button
          onClick={() => setTab('compliance')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            tab === 'compliance' ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-400 hover:text-gray-600'
          )}
        >
          <ShieldCheck className="w-4 h-4 inline mr-2" />
          Compliance Summary
        </button>
      </div>

      {/* Schedules tab */}
      {tab === 'schedules' && (
        <>
          <div className="flex gap-3">
            <button
              onClick={() => { setComplianceOnly(!complianceOnly); setPage(1) }}
              className={cn('btn-secondary text-xs', complianceOnly && 'border-purple-400 text-purple-700 bg-purple-50')}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Compliance only
            </button>
          </div>

          <div className="card p-0 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
              </div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No PPM schedules found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Schedule</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Asset</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Frequency</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Next Due</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Last Done</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Owner</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-500">WOs</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {schedules.map((ppm) => (
                      <tr key={ppm.id} className={cn('hover:bg-gray-50/50 transition-colors', ppm.isOverdue && 'bg-red-50/30')}>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            {ppm.isCompliance && (
                              <ShieldCheck className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium text-navy-800">{ppm.title}</p>
                              {ppm.complianceCategory && (
                                <span className="badge bg-purple-100 text-purple-800 mt-0.5">
                                  {ppm.complianceCategory.code}
                                </span>
                              )}
                              {ppm.regulationRef && (
                                <p className="text-xs text-gray-400 mt-0.5">{ppm.regulationRef}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {ppm.asset ? (
                            <Link to={`/assets/${ppm.asset.id}`} className="hover:text-navy-600">
                              <div className="flex items-center gap-1.5">
                                <div className={cn('w-2 h-2 rounded-full', CRITICALITY_DOT[ppm.asset.criticality])} />
                                <span className="text-navy-800">{ppm.asset.name}</span>
                              </div>
                              <p className="text-xs text-gray-400 font-mono">{ppm.asset.code}</p>
                            </Link>
                          ) : (
                            <span className="text-gray-400">\u2014</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="badge bg-gray-100 text-gray-700">
                            {FREQUENCY_LABELS[ppm.frequency] ?? ppm.frequency}
                          </span>
                          {ppm.estimatedMinutes && (
                            <p className="text-xs text-gray-400 mt-0.5">{ppm.estimatedMinutes}m est.</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className={cn('text-sm font-medium',
                              ppm.isOverdue ? 'text-red-600' : ppm.daysUntilDue <= 7 ? 'text-amber-600' : 'text-gray-700'
                            )}>
                              {formatDate(ppm.nextDueDate)}
                            </p>
                            {ppm.isOverdue ? (
                              <span className="badge bg-red-100 text-red-800 mt-0.5">
                                {Math.abs(ppm.daysUntilDue)}d overdue
                              </span>
                            ) : ppm.daysUntilDue <= 7 ? (
                              <span className="badge bg-amber-100 text-amber-800 mt-0.5">
                                {ppm.daysUntilDue}d remaining
                              </span>
                            ) : (
                              <p className="text-xs text-gray-400">{ppm.daysUntilDue}d remaining</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {formatDate(ppm.lastCompletedAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {ppm.owner
                            ? `${ppm.owner.firstName} ${ppm.owner.lastName}`
                            : ppm.vendor?.name ?? <span className="text-gray-300">\u2014</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {ppm._count.workOrders > 0 ? (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <Wrench className="w-3.5 h-3.5" />
                              {ppm._count.workOrders}
                            </span>
                          ) : (
                            <span className="text-gray-300">\u2014</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setCompleteId(ppm.id)}
                            className="btn-secondary py-1 px-2.5 text-xs"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Complete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} schedules)
                </p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= pagination.totalPages}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Compliance tab */}
      {tab === 'compliance' && (
        <div className="space-y-4">
          {complianceLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
            </div>
          ) : !compliance || compliance.length === 0 ? (
            <div className="text-center py-16">
              <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No compliance categories configured</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {compliance.map((cat) => (
                <div
                  key={cat.id}
                  className={cn('card', {
                    'border-green-200': cat.status === 'COMPLIANT',
                    'border-amber-200': cat.status === 'DUE_SOON',
                    'border-red-300 bg-red-50/30': cat.status === 'OVERDUE',
                  })}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-navy-800">{cat.name}</h3>
                    <span className={cn('badge', {
                      'bg-green-100 text-green-800': cat.status === 'COMPLIANT',
                      'bg-amber-100 text-amber-800': cat.status === 'DUE_SOON',
                      'bg-red-100 text-red-800': cat.status === 'OVERDUE',
                    })}>
                      {cat.status === 'COMPLIANT' ? 'Compliant' : cat.status === 'DUE_SOON' ? 'Due Soon' : 'Overdue'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Certificates</span>
                      <span className="text-navy-800">{cat.certificates.length}</span>
                    </div>
                    {cat.upcomingExpiries.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-amber-600">Expiring soon</span>
                        <span className="font-medium text-amber-600">{cat.upcomingExpiries.length}</span>
                      </div>
                    )}
                    {cat.overdueSchedules.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-red-600">Overdue inspections</span>
                        <span className="font-medium text-red-600">{cat.overdueSchedules.length}</span>
                      </div>
                    )}
                  </div>

                  {cat.status !== 'COMPLIANT' && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {cat.upcomingExpiries.slice(0, 2).map((cert: any) => (
                        <p key={cert.id} className="text-xs text-gray-500">
                          <AlertTriangle className="w-3 h-3 inline text-amber-500 mr-1" />
                          {cert.certificateNumber ?? 'Certificate'} expires {formatDate(cert.expiresAt)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Complete modal */}
      {completeId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-navy-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Complete PPM Schedule
            </h2>

            <div className="space-y-4">
              <div>
                <label className="label">Completion Notes</label>
                <textarea
                  value={completeNotes}
                  onChange={(e) => setCompleteNotes(e.target.value)}
                  rows={3}
                  className="input resize-none"
                  placeholder="Notes on work performed, findings, issues..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Labour Cost</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={labourCost}
                    onChange={(e) => setLabourCost(e.target.value)}
                    className="input"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="label">Parts Cost</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={partsCost}
                    onChange={(e) => setPartsCost(e.target.value)}
                    className="input"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setCompleteId(null); setCompleteNotes(''); setLabourCost(''); setPartsCost('') }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => completeMutation.mutate(completeId)}
                disabled={completeMutation.isPending}
                className="btn-primary"
              >
                {completeMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Completing...</>
                ) : (
                  <><CheckCircle className="w-4 h-4" /> Mark Complete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
